import { Controller, useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import FormField from "@/components/ui/formField";
import useWidgetConfiguration from "@/hooks/dataset-widget/useWidgetConfiguration";
import { AggregationType, WidgetType } from "@/types/widget";
import { placeHolderMap } from "@/utils/widget";

import { WidgetFormData } from ".";

interface FormWidgetConfigurationProps {
  widgetType: WidgetType;
  onClickPreview: () => void;
  isEditing: boolean;
  analysisId?: string;
}

const FormWidgetConfiguration = ({
  widgetType,
  onClickPreview,
  isEditing,
  analysisId
}: FormWidgetConfigurationProps) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { isValid, isDirty, defaultValues },
    reset
  } = useFormContext<WidgetFormData>();

  const watchDatasetId = watch("dataset_id");
  const watchMeasure = watch("measure");
  const watchDimension = watch("dimension");

  const {
    datasetOptions,
    dimensionOptions,
    measureOptions,
    breakdownGroupOptions,
    aggregationOptions,
    orderByOptions,
    dimensionValueShownOptions,
    isTimeSeriesDataset
  } = useWidgetConfiguration({
    datasetId: watchDatasetId,
    widgetType,
    measure: watchMeasure,
    dimension: watchDimension,
    analysisId
  });

  const isBreakdownGroup = (value: string) =>
    breakdownGroupOptions.some((option) => option.value === value);

  // A breakdown-group dimension offers only that same group as measure, and DONUT
  // never offers groups — a measure that is no longer offered must not be submitted.
  const isMeasureStillOffered = (nextDimension: string) => {
    if (isBreakdownGroup(nextDimension)) return watchMeasure === nextDimension;
    return !isBreakdownGroup(watchMeasure) || widgetType !== WidgetType.DONUT;
  };

  const clearMeasure = () => {
    setValue("measure", "");
    // An empty measure shrinks aggregationOptions to COUNT variants only, so a
    // non-COUNT value would stay in form state while vanishing from the dropdown.
    setValue("aggregation", AggregationType.COUNT, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const orderByDropdown = (
    <Controller
      name="configuration.order_by"
      control={control}
      render={({ field: { value, onChange } }) => (
        <Dropdown
          name="order_by"
          value={
            orderByOptions.find((option) => option.value === value) || null
          }
          placeholder={isTimeSeriesDataset ? "Order by Dimension" : "Order by"}
          data={orderByOptions}
          onSelect={(val) => {
            onChange(val?.value);
          }}
          disabled={!watchDatasetId}
        />
      )}
    />
  );

  return (
    <div className="flex-shrink-0 flex-grow-0 border border-whisper rounded-lg p-4 h-full overflow-y-auto max-w-[300px]">
      <div className="flex flex-col justify-between gap-4 h-full">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-semibold text-txtHeading">
              Configuration
            </h1>
            <Button
              className="!no-underline text-sm font-semibold text-teal-500 "
              variant="link"
              onClick={() => {
                reset(defaultValues);
              }}
              disabled={!isDirty}
            >
              Reset
            </Button>
          </div>
          <p className="text-xs text-txtBody">
            Select a data source to generate visual insights from dataset
          </p>

          <FormField
            register={register}
            name="label"
            placeHolder="Label"
            rules={{ required: true }}
          />
          <div>
            <Controller
              name="dataset_id"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  value={
                    datasetOptions.find((option) => option.value === value) ||
                    null
                  }
                  placeholder="Data source"
                  textTransform="capitalize-words"
                  data={datasetOptions}
                  onSelect={(val) => {
                    onChange(val?.value);
                    setValue("dimension", "");
                    setValue("measure", "");
                  }}
                />
              )}
            />
            <span className="text-[10px] text-txtBody px-2">
              Select available data source
            </span>
          </div>

          {/* Data Configuration */}
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold text-txtPlaceholder">
              Data Configuration
            </p>
            {widgetType !== WidgetType.BIG_NUMBER && (
              <Controller
                name="dimension"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    key={watchDatasetId}
                    name="dimension"
                    value={
                      dimensionOptions.find(
                        (option) => option.value === value
                      ) || null
                    }
                    placeholder={placeHolderMap[widgetType].dimension}
                    data={dimensionOptions}
                    onSelect={(val) => {
                      const newDimension = val?.value ?? "";
                      onChange(newDimension);
                      if (watchMeasure && !isMeasureStillOffered(newDimension))
                        clearMeasure();
                    }}
                    disabled={!watchDatasetId}
                  />
                )}
              />
            )}
            <div>
              <Controller
                name="measure"
                control={control}
                rules={{
                  validate: (value) =>
                    (widgetType !== WidgetType.BIG_NUMBER &&
                      widgetType !== WidgetType.LINE) ||
                    Boolean(value)
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    key={watchDatasetId}
                    name="measure"
                    value={
                      measureOptions.find((option) => option.value === value) ||
                      null
                    }
                    placeholder={placeHolderMap[widgetType].measure}
                    data={measureOptions}
                    onSelect={(val) => {
                      const newMeasure = val?.value ?? "";
                      onChange(newMeasure);
                      // Clearing measure shrinks aggregationOptions to COUNT
                      // variants only. Any non-COUNT value (SUM/AVG/...) stays
                      // in form state but disappears from the dropdown UI —
                      // reset to COUNT so the submitted payload matches what
                      // the user can actually see.
                      if (!newMeasure) {
                        setValue("aggregation", AggregationType.COUNT, {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }
                    }}
                    disabled={!watchDatasetId}
                    withSearch={
                      widgetType !== WidgetType.BIG_NUMBER &&
                      widgetType !== WidgetType.LINE
                    }
                  />
                )}
              />
              {(widgetType === WidgetType.BAR_H ||
                widgetType === WidgetType.BAR_V ||
                widgetType === WidgetType.DONUT) && (
                <span className="text-[10px] text-txtBody px-2">Optional</span>
              )}
            </div>
            <Controller
              name="aggregation"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  name="aggregation"
                  value={
                    aggregationOptions.find(
                      (option) => option.value === value
                    ) || null
                  }
                  placeholder="Aggregation"
                  data={aggregationOptions}
                  onSelect={(val) => {
                    onChange(val?.value);
                  }}
                  disabled={!watchDatasetId}
                />
              )}
            />
            {(widgetType === WidgetType.BAR_H ||
              widgetType === WidgetType.BAR_V ||
              widgetType === WidgetType.LINE) &&
              orderByDropdown}
            {(widgetType === WidgetType.BAR_H ||
              widgetType === WidgetType.BAR_V ||
              widgetType === WidgetType.DONUT) && (
              <Controller
                name="configuration.dimension_values_shown"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    name="dimension_values_shown"
                    value={
                      dimensionValueShownOptions.find(
                        (option) => Number(option.value) === value
                      ) || null
                    }
                    placeholder={
                      placeHolderMap[widgetType].dimension_values_shown
                    }
                    data={dimensionValueShownOptions}
                    onSelect={(val) => {
                      onChange(val ? Number(val.value) : undefined);
                    }}
                    disabled={!watchDatasetId}
                  />
                )}
              />
            )}
            {widgetType === WidgetType.DONUT && orderByDropdown}
          </div>
        </div>
        <div className="flex flex-col">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={!isValid}
            onClick={onClickPreview}
          >
            Preview
          </Button>
          <Button className="mt-2 rounded-xl" type="submit" disabled={!isValid}>
            {isEditing ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormWidgetConfiguration;
